import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Tooltip } from "antd";
import {
  border,
  radius,
  spacing,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/**
 * Compact "Version N · Updated X ago" pill for the document workspace
 * header. Renders nothing when version is missing — every other DMS
 * surface tolerates a null version field, so the pill should fail
 * closed (hidden) rather than render a half-shaped chip.
 *
 * Mirrors the pure-JS `formatRelative` helper in
 * `src/components/AIPane/ContextAnalysisTab.js` so we don't pull
 * dayjs/date-fns into the document viewer's bundle for one chip.
 */

const PillWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[0.5]} ${spacing[2]};
  margin-left: ${spacing[3]};
  background: ${surface.hover};
  border: 1px solid ${border.light};
  border-radius: ${radius.sm};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  color: ${text.secondary};
  white-space: nowrap;
  vertical-align: middle;
  ${({ $clickable }) =>
    $clickable
      ? `
  cursor: pointer;
  &:hover { border-color: #9db7d9; background: #f3f7fc; }
  &:focus-visible { outline: 2px solid #4a7fc1; outline-offset: 1px; }
`
      : ""}
`;

const VersionLabel = styled.span`
  color: ${text.primary};
  font-weight: ${typography.weight.semibold};
`;

const Separator = styled.span`
  color: ${text.muted};
  font-weight: ${typography.weight.regular};
`;

const RelativeLabel = styled.span`
  color: ${text.secondary};
`;

export const formatLastEditedRelative = (isoString) => {
  if (!isoString) return null;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return null;
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return "just now";
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(ts).toISOString().slice(0, 10);
};

const formatAbsoluteTooltip = (isoString) => {
  if (!isoString) return null;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return null;
  return new Date(ts).toLocaleString();
};

const DocumentVersionPill = ({ version, lastModified, onClick }) => {
  // `version` is a non-negative integer counter on Document. Anything
  // else (null/undefined/0/string/non-finite/negative) means we don't
  // have meaningful version metadata — render nothing rather than a
  // misleading "Version 0" chip.
  const numericVersion =
    typeof version === "number" ? version : Number(version);
  if (
    !Number.isFinite(numericVersion) ||
    numericVersion < 1 ||
    !Number.isInteger(numericVersion)
  ) {
    return null;
  }

  const relative = formatLastEditedRelative(lastModified);
  const absolute = formatAbsoluteTooltip(lastModified);

  const pill = (
    <PillWrapper
      data-testid="document-version-pill"
      $clickable={Boolean(onClick)}
      {...(onClick
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": `Version ${numericVersion}. Open version history`,
            onClick,
            onKeyDown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(event);
              }
            },
          }
        : {})}
    >
      <VersionLabel>Version {numericVersion}</VersionLabel>
      {relative && (
        <>
          <Separator>·</Separator>
          <RelativeLabel>Updated {relative}</RelativeLabel>
        </>
      )}
    </PillWrapper>
  );

  if (absolute) {
    return (
      <Tooltip
        title={
          onClick
            ? `Last edited ${absolute}. Click for version history.`
            : `Last edited ${absolute}`
        }
        placement="bottomLeft"
        mouseEnterDelay={0.4}
      >
        {pill}
      </Tooltip>
    );
  }

  return pill;
};

DocumentVersionPill.propTypes = {
  version: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lastModified: PropTypes.string,
  // When given, the pill becomes a button that opens the version history.
  onClick: PropTypes.func,
};

DocumentVersionPill.defaultProps = {
  version: null,
  lastModified: null,
  onClick: null,
};

export default DocumentVersionPill;
